import { Avatar, Badge, Button, Center, Container, Group, Loader, Paper, Select, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { collection, collectionGroup, doc, getDocFromServer, getDocs, limit, orderBy, query, startAfter } from 'firebase/firestore';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { CopyableId } from '../../components';
import { db } from '../../firebase-config';
import styles from './staffs.module.css';

const getStaffName = (staff) => {
    return staff.name || staff.displayName || staff.fullName || staff.email || staff.phone || 'Unnamed staff';
};

const getStaffContact = (staff) => {
    return staff.email || staff.phone || staff.username || '-';
};

const getStaffPosition = (staff) => {
    if (staff.position) return staff.position;
    if (staff.role) return staff.role;
    if (staff.title) return staff.title;
    if (staff.permission) return staff.permission;
    if (staff.type) return staff.type;
    if (staff.manager || staff.isManager) return 'Manager';
    if (staff.owner || staff.isOwner) return 'Owner';
    return 'Staff';
};

const normalize = (value) => String(value || '').toLowerCase();

const sortByStaffName = (rows) => {
    return [...rows].sort((a, b) => {
        const nameCompare = a.staffName.localeCompare(b.staffName, undefined, { sensitivity: 'base' });
        if (nameCompare !== 0) return nameCompare;

        return a.brandName.localeCompare(b.brandName, undefined, { sensitivity: 'base' });
    });
};

export function Staffs() {
    const [staffRows, setStaffRows] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [brandFilter, setBrandFilter] = useState('All');
    const [activePage, setActivePage] = useState(1);
    const [pageSize, setPageSize] = useState('25');
    const [hasNextPage, setHasNextPage] = useState(false);
    const pageCursors = useRef([]);
    const profileCache = useRef(new Map());
    const brandCache = useRef(new Map());

    useEffect(() => {
        fetchBrands();
    }, []);

    useEffect(() => {
        fetchStaffsPage();
    }, [activePage, brandFilter, pageSize]);

    const fetchStaffProfile = async (staffId) => {
        if (profileCache.current.has(staffId)) {
            return profileCache.current.get(staffId);
        }

        try {
            const profileSnapshot = await getDocFromServer(doc(db, 'staffs', staffId));
            const profile = profileSnapshot.exists() ? profileSnapshot.data() : {};
            profileCache.current.set(staffId, profile);
            return profile;
        } catch (err) {
            profileCache.current.set(staffId, {});
            return {};
        }
    };

    const getBrand = async (brandId) => {
        if (brandCache.current.has(brandId)) {
            return brandCache.current.get(brandId);
        }

        const cachedBrand = brands.find((brand) => brand.id === brandId);
        if (cachedBrand) {
            brandCache.current.set(brandId, cachedBrand);
            return cachedBrand;
        }

        const brandSnapshot = await getDocFromServer(doc(db, 'brands', brandId));
        const brand = brandSnapshot.exists()
            ? { ...brandSnapshot.data(), id: brandId }
            : { id: brandId, name: 'Unknown brand' };
        brandCache.current.set(brandId, brand);
        return brand;
    };

    const fetchBrands = async () => {
        try {
            const brandsSnapshot = await getDocs(query(collection(db, 'brands'), orderBy('name')));
            const fetchedBrands = brandsSnapshot.docs.map((brandDoc) => ({
                ...brandDoc.data(),
                id: brandDoc.id
            }));

            fetchedBrands.forEach((brand) => brandCache.current.set(brand.id, brand));
            setBrands(fetchedBrands);
        } catch (err) {
            setError(err.message);
        }
    };

    const buildStaffRows = async (staffDocs) => {
        return Promise.all(staffDocs.map(async (staffDoc) => {
            const brandId = staffDoc.ref.parent.parent?.id || brandFilter;
            const [profile, brand] = await Promise.all([
                fetchStaffProfile(staffDoc.id),
                getBrand(brandId)
            ]);
            const staff = {
                ...profile,
                ...staffDoc.data(),
                id: staffDoc.id
            };

            return {
                id: staffDoc.ref.path,
                brandId,
                brandName: brand.name || 'Unnamed brand',
                brandLogo: brand.logo,
                staffId: staffDoc.id,
                staffName: getStaffName(staff),
                contact: getStaffContact(staff),
                position: getStaffPosition(staff)
            };
        }));
    };

    const resetPagination = () => {
        pageCursors.current = [];
        setActivePage(1);
    };

    const fetchStaffsPage = async () => {
        setLoading(true);
        setError('');

        try {
            const rowsPerPage = Number(pageSize);
            const cursor = activePage > 1 ? pageCursors.current[activePage - 2] : null;

            if (activePage > 1 && !cursor) {
                setActivePage(1);
                return;
            }

            const baseQuery = brandFilter === 'All'
                ? collectionGroup(db, 'staffs')
                : collection(db, 'brands', brandFilter, 'staffs');
            const constraints = cursor
                ? [startAfter(cursor), limit(rowsPerPage + 1)]
                : [limit(rowsPerPage + 1)];
            const staffSnapshot = await getDocs(query(baseQuery, ...constraints));
            const hasMore = staffSnapshot.docs.length > rowsPerPage;
            const pageDocs = hasMore ? staffSnapshot.docs.slice(0, rowsPerPage) : staffSnapshot.docs;
            const rows = sortByStaffName(await buildStaffRows(pageDocs));

            if (pageDocs.length > 0) {
                pageCursors.current[activePage - 1] = pageDocs[pageDocs.length - 1];
            }

            setHasNextPage(hasMore);
            setStaffRows(rows);
        } catch (err) {
            setError(err.message);
            setStaffRows([]);
            setHasNextPage(false);
        } finally {
            setLoading(false);
        }
    };

    const filteredRows = useMemo(() => {
        const searchTerm = normalize(search);

        return staffRows.filter((row) => {
            const matchesBrand = brandFilter === 'All' || row.brandId === brandFilter;
            const matchesSearch = !searchTerm || [
                row.staffName,
                row.contact,
                row.position,
                row.brandName,
                row.staffId,
                row.brandId
            ].some((field) => normalize(field).includes(searchTerm));

            return matchesBrand && matchesSearch;
        });
    }, [brandFilter, search, staffRows]);

    const rowsPerPage = Number(pageSize);
    const pageStart = (activePage - 1) * rowsPerPage;
    const visibleStart = filteredRows.length === 0 ? 0 : pageStart + 1;
    const visibleEnd = pageStart + filteredRows.length;

    const brandOptions = [
        { value: 'All', label: 'All brands' },
        ...brands.map((brand) => ({ value: brand.id, label: brand.name || brand.id }))
    ];

    return (
        <>
            <Helmet>
                <title>Lollet Admin - Staffs</title>
            </Helmet>
            <Container size="xxl" className={styles.container}>
                <Stack gap="lg">
                    <Group justify="space-between" align="flex-end">
                        <div>
                            <Title order={1}>Staffs</Title>
                            <Text c="dimmed" mt={4}>
                                View staff access by brand and position.
                            </Text>
                        </div>
                        <Group gap="sm" className={styles.filters}>
                            <TextInput
                                leftSection={<IconSearch size={16} />}
                                placeholder="Search staffs"
                                value={search}
                                onChange={(event) => setSearch(event.currentTarget.value)}
                            />
                            <Select
                                data={brandOptions}
                                value={brandFilter}
                                onChange={(value) => {
                                    setBrandFilter(value || 'All');
                                    resetPagination();
                                }}
                                allowDeselect={false}
                                searchable
                            />
                        </Group>
                    </Group>

                    {error && <Text c="red" ta="center">{error}</Text>}

                    <Paper withBorder radius="md" p="md">
                        {loading ? (
                            <Center py="xl">
                                <Loader />
                            </Center>
                        ) : (
                            <>
                                <Group justify="space-between" mb="md">
                                    <div>
                                        <Text fw={700}>Page {activePage}</Text>
                                        <Text fz="sm" c="dimmed">
                                            Showing {visibleStart}-{visibleEnd}{search ? ' matching this page' : ''}
                                        </Text>
                                    </div>
                                    <Group gap="sm">
                                        <Select
                                            data={[
                                                { value: '10', label: '10 / page' },
                                                { value: '25', label: '25 / page' },
                                                { value: '50', label: '50 / page' },
                                                { value: '100', label: '100 / page' }
                                            ]}
                                            value={pageSize}
                                            onChange={(value) => {
                                                setPageSize(value || '25');
                                                resetPagination();
                                            }}
                                            allowDeselect={false}
                                            w={130}
                                        />
                                        <Badge variant="light" color="yellow">
                                            {hasNextPage ? 'More available' : 'End reached'}
                                        </Badge>
                                    </Group>
                                </Group>
                                <Table.ScrollContainer minWidth={900}>
                                    <Table verticalSpacing="sm" striped>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Staff</Table.Th>
                                                <Table.Th>Contact</Table.Th>
                                                <Table.Th>Brand</Table.Th>
                                                <Table.Th>Position</Table.Th>
                                                <Table.Th>Copy</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {filteredRows.length > 0 ? filteredRows.map((row) => (
                                                <Table.Tr key={row.id}>
                                                    <Table.Td>
                                                        <Text fz="sm" fw={600} c={row.error ? 'red' : undefined}>
                                                            {row.staffName}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text fz="sm" c={row.contact === '-' ? 'dimmed' : undefined}>
                                                            {row.contact}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Group gap="sm" wrap="nowrap">
                                                            <Avatar size={30} src={row.brandLogo} radius={6} />
                                                            <Text fz="sm" fw={500}>{row.brandName}</Text>
                                                        </Group>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Badge variant="light" color={row.position.toLowerCase().includes('manager') ? 'yellow' : 'gray'}>
                                                            {row.position}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Group gap="xs" wrap="nowrap">
                                                            <CopyableId id={row.staffId} label="Staff ID" />
                                                            <CopyableId id={row.brandId} label="Brand ID" />
                                                        </Group>
                                                    </Table.Td>
                                                </Table.Tr>
                                            )) : (
                                                <Table.Tr>
                                                    <Table.Td colSpan={5}>
                                                        <Text c="dimmed" ta="center" py="xl">
                                                            No staffs found.
                                                        </Text>
                                                    </Table.Td>
                                                </Table.Tr>
                                            )}
                                        </Table.Tbody>
                                    </Table>
                                </Table.ScrollContainer>
                                <Group justify="center" mt="lg">
                                    <Button
                                        disabled={activePage === 1}
                                        onClick={() => setActivePage((page) => Math.max(1, page - 1))}
                                        variant="light"
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        disabled={!hasNextPage}
                                        onClick={() => setActivePage((page) => page + 1)}
                                    >
                                        Next
                                    </Button>
                                </Group>
                            </>
                        )}
                    </Paper>
                </Stack>
            </Container>
        </>
    );
}
